import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import {
	Assignment,
	ConversationTemplate,
	DefaultTemplate,
	deleteDefault,
	getConversationTemplates,
	getDefaultTemplateByAssignmentId,
} from "../../api";
import { useGlobalState } from "../globalState";

const AssignmentForm = lazy(() => import("./AssignmentForm"));

interface AssignmentConfigPanelProps {
	assignment: Assignment;
	assignments: Assignment[];
	onSave: (assignment: Assignment) => Promise<Assignment>;
	id?: string;
	tabQueryParam?: string;
}

const AssignmentConfigPanel = ({
	assignment,
	assignments,
	onSave,
	id,
	tabQueryParam = "tab",
}: AssignmentConfigPanelProps) => {
	const { users, loading } = useGlobalState();
	const [defaultTemplate, setDefaultTemplate] = useState<DefaultTemplate>(null);
	const [templates, setTemplates] = useState<ConversationTemplate[]>([]);
	const [dataLoading, setDataLoading] = useState(false);

	const refreshDefaultTemplate = async () => {
		setDataLoading(true);
		try {
			const newTemplates = await getConversationTemplates();
			setTemplates(newTemplates);
			if (!assignment || !assignment.id) {
				setDefaultTemplate(null);
				return;
			}
			const res = await getDefaultTemplateByAssignmentId({
				assignmentId: assignment.id,
			});
			setDefaultTemplate(res.results[0] ?? null);
		} finally {
			setDataLoading(false);
		}
	};

	useEffect(() => {
		if (assignment) refreshDefaultTemplate();
	}, [assignment]);

	const handleDeleteDefault = () => {
		deleteDefault(defaultTemplate.id)
			.then(() => {
				setDefaultTemplate(null);
				refreshDefaultTemplate();
			})
			.catch((err) => console.log(err));
	};

	if (loading || dataLoading) {
		return (
			<Box id={id ? `${id}-loading` : undefined}>
				<Stack spacing={2}>
					<Typography>Loading content</Typography>
					<LinearProgress />
				</Stack>
			</Box>
		);
	}

	return (
		<Suspense fallback={<LinearProgress />}>
			<AssignmentForm
				id={id ? `${id}-edit-form` : undefined}
				users={users}
				assignment={assignment}
				assignments={assignments}
				onSave={onSave}
				conversationTemplates={templates}
				defaultTemplateId={defaultTemplate?.template || null}
				onDefaultTemplateChanged={refreshDefaultTemplate}
				onDeleteDefault={handleDeleteDefault}
				tabQueryParam={tabQueryParam}
			/>
		</Suspense>
	);
};

export default AssignmentConfigPanel;
